import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import useProductPage from 'src/hooks/useProductPage'
import useSaveAsDashboard from 'src/hooks/useSaveAsDashboard'
import useMenusStore from 'src/stores/useMenusStore'
import { findDashboardsOnMenusFn } from 'src/utils/menuUtils'
import InputModal from '../InputModal'

const DASHBOARD_LABEL_MIN_LENGTH = 3
const DASHBOARD_LABEL_MAX_LENGTH = 33
const LabelErrorMessage = {
  max: 'Dashboard name must not exceed 30 characters',
  min: 'Dashboard name must exceed 2 characters',
}

interface ModalSaveAsDashboardProps {
  isOpen: boolean
  onClose: () => void
}

const ModalSaveAsDashboard: FC<ModalSaveAsDashboardProps> = ({ isOpen, onClose }) => {
  const { product, code, program } = useProductPage()
  const { menus } = useMenusStore()
  const { handleSaveAsDashboard } = useSaveAsDashboard()
  const [labelError, setLabelError] = useState(false)

  const existedDashboardNames = useMemo(() => {
    const existingDashboards = findDashboardsOnMenusFn({
      menus,
      product,
      subProductCode: code,
      programId: program?.toString(),
    })

    return existingDashboards.map(d => d.name.toUpperCase().trim()) || []
  }, [menus, product, code, program])

  const labelCheck = useCallback(
    (label: string): boolean => {
      const formattedLabel = label.toUpperCase().trim()
      const sameDashboardLabel = existedDashboardNames.includes(formattedLabel)
      if (sameDashboardLabel) {
        setLabelError(true)
        toast.error('A query already has this name')
        return true
      }

      if (formattedLabel.length < DASHBOARD_LABEL_MIN_LENGTH) {
        setLabelError(true)
        toast.error(LabelErrorMessage.min)
        return true
      }

      if (formattedLabel.length > DASHBOARD_LABEL_MAX_LENGTH) {
        setLabelError(true)
        toast.error(LabelErrorMessage.max)
        return true
      }

      setLabelError(false)
      return false
    },
    [existedDashboardNames],
  )

  const [name, setName] = useState('')

  const handleUpdateDashboard = async () => {
    if (!name) return
    if (labelCheck(name)) return
    onClose()
    await handleSaveAsDashboard(name)
  }

  useEffect(() => {
    if (isOpen) {
      setName('')
      setLabelError(false)
    }
  }, [isOpen])

  return (
    <InputModal
      title="Save As"
      buttonLabel="Save"
      open={isOpen}
      onClose={onClose}
      value={name || ''}
      onChange={evt => setName(evt.target.value)}
      submit={handleUpdateDashboard}
      hasError={labelError}
    />
  )
}

export default ModalSaveAsDashboard
